import { useCallback, useEffect, useMemo, useState } from 'react';
import '../../backlog/backlog.css';
import '../../modules/module.css';
import './quotes.css';
import { api } from '../../../api/client';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../../components/AppDataGrid';
import { AppToolbar, ExportMenu } from '../../../components/AppToolbar';
import { useConfirm } from '../../../components/confirm';
import { FormTitle } from '../../../components/FormTitle';
import { GridSearch } from '../../../components/GridSearch';
import { useHotkeys } from '../../../components/hotkeys';
import { useNotify } from '../../../components/notifications';
import { downloadQuoteReport, reactivateQuotes, reportErrorMessage } from './detailApi';
import CanceledQuoteDetailPage from './CanceledQuoteDetailPage';

// ─────────────────────────────────────────────────────────────────────────────
// CRM ▸ Accounts ▸ Quotes ▸ Canceled Quotes (nav `crm-quotes-canceled`).
// Port of legacy ALPACA `CRMQuotes_ListView_Cancel` (BTLIMS.Web/Model.xafml
// :13877). A READ-ONLY list (legacy AllowNew/Edit/Delete/Link/Unlink all False)
// with two form-specific toolbar actions, in the legacy order:
//
//   Preview · Reactive · Refresh · Export to
//
// The list DATA is the shared ModuleRegistry entry (`GET /api/v1/modules/
// crm-quotes-canceled`) that first wired the grid; this page adds ONLY the two
// actions, so it touches no other quotes form (owner request 2026-10-29).
//
//   Preview  — legacy `btnqrpreview`: exactly ONE selected quote → its PDF
//              report (the SAME `GET /quotes/{id}/report` Open Quotes uses).
//   Reactive — legacy `QuoteReactive_Execute` :7112 → `Dcreason_Accepting`
//              :4684: require a reason, then un-cancel the selected quote(s)
//              (Status → Pending Submission), so they return to Open Quotes.
//              `POST /quotes/reactivate`.
//
// AUDIT. Reactivate writes under the "Open Quotes" FormName — the name the quote
// lifecycle already audits by (QuotesController).
// ─────────────────────────────────────────────────────────────────────────────

const PAGE_ID = 'crm-quotes-canceled';

// `toLocaleString` on a NUMBER is digit grouping, not a date (C2 bans only the
// locale DATE methods). Same currency mask the Open Quotes grid uses.
const CURRENCY = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

/** One row as the ModuleRegistry `crm-quotes-canceled` entry returns it. */
interface CanceledQuoteRow {
  oid: string;
  date: string | null;
  quoteId: string | null;
  title: string | null;
  client: string | null;
  prospect: boolean;
  amount: number | null;
  expirationDate: string | null;
  status: string | null;
}

// Legacy CRMQuotes_ListView_Cancel visible columns (model Index 0-7).
const COLUMNS: AppColumn<CanceledQuoteRow>[] = [
  { field: 'date', header: 'Date', width: 120, type: 'date' },
  { field: 'quoteId', header: 'QuoteID', width: 130 },
  { field: 'title', header: 'Title', width: 200 },
  // The [NonPersistent] NonClient — prospect name or customer name.
  { field: 'client', header: 'Client', width: 180 },
  {
    field: 'prospect', header: 'Prospect', width: 90, align: 'center',
    // A tick box, as legacy shows the IsProspect flag - never the word "false".
    render: (r) => <input type="checkbox" checked={!!r.prospect} readOnly disabled aria-label="Prospect" />,
  },
  {
    field: 'amount', header: 'Amount', width: 120, align: 'right', type: 'number',
    // A null stays blank rather than "$0.00", which would assert a price nobody quoted.
    render: (r) => (r.amount == null ? '' : CURRENCY.format(r.amount)),
  },
  { field: 'expirationDate', header: 'ExpirationDate', width: 140, type: 'date' },
  // The one column allowed colour (§18.6); the label is resolved server-side.
  { field: 'status', header: 'Status', width: 130, type: 'status' },
];

export default function CanceledQuotesPage({ onHome }: { onHome?: () => void }) {
  const notify = useNotify();
  const confirm = useConfirm();
  const [rows, setRows] = useState<CanceledQuoteRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [busy, setBusy] = useState(false);
  // Double-click (or Enter on) a row opens the READ-ONLY detail view.
  const [openId, setOpenId] = useState<string | null>(null);
  // Grid maximize is driven from the search-row button (controlled, below).
  const [maxed, setMaxed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const t0 = performance.now();
    try {
      const res = await api.get<{ data?: CanceledQuoteRow[]; rows?: CanceledQuoteRow[] }>(
        `/api/v1/modules/${PAGE_ID}`, { params: { pageSize: 1000 } });
      const data = res.data?.data ?? res.data?.rows ?? [];
      setRows(Array.isArray(data) ? data : []);
      // A reload can drop rows; a surviving selection would point at nothing.
      setSelected(new Set());
    } catch (e) {
      notify.error(e instanceof Error ? e.message : 'Failed to load canceled quotes.');
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { void load(); }, [load]);

  // The Search box filters the rows already fetched (same as the Open Quotes list).
  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.quoteId, r.title, r.client, r.status].some((v) => (v ?? '').toLowerCase().includes(q)));
  }, [rows, search]);

  /** Preview — legacy `btnqrpreview`, which needs exactly ONE selected quote
   *  (`SelectionDependencyType.RequireSingleObject`). */
  async function preview() {
    if (selected.size !== 1) {
      // Legacy wording, verbatim (LDMMessages "selectchkbox").
      notify.warning('Please select an item to proceed further.');
      return;
    }
    try {
      const filename = await downloadQuoteReport(String([...selected][0]));
      notify.success(`${filename} downloaded.`);
    } catch (e) {
      notify.error((await reportErrorMessage(e)) ?? 'Failed to generate the quote report.');
    }
  }

  /** Reactive — legacy `QuoteReactive_Execute` → `Dcreason_Accepting`: a reason is
   *  required (LDMMessages "Quotereason"), then each selected quote is un-cancelled
   *  and returns to Open Quotes. */
  async function reactive() {
    const ids = [...selected].map(String);
    if (ids.length === 0) {
      notify.warning('Please select an item to proceed further.');
      return;
    }
    const reason = await confirm.prompt({
      title: 'Reactive Reason',
      message: ids.length === 1
        ? 'Enter a reason to reactivate this quote:'
        : `Enter a reason to reactivate ${ids.length} quotes:`,
      placeholder: 'Reason…',
      required: true,
    });
    if (!reason) return;   // cancelled, or empty (the required prompt blocks empty)
    setBusy(true);
    try {
      const r = await reactivateQuotes(ids, reason);
      notify.success(r.reactivated === 1
        ? 'Quote reactivated successfully.'
        : `${r.reactivated} quotes reactivated successfully.`);
      await load();
    } catch (e) {
      notify.error(e instanceof Error ? e.message : 'Failed to reactivate.');
    } finally {
      setBusy(false);
    }
  }

  // Preview / Refresh / Export auto-register through <AppToolbar>; Reactive is a
  // form-specific button, so it registers its own hotkey here (standing rule 14).
  useHotkeys([
    {
      combo: 'alt+k', label: 'Reactive', group: 'Canceled Quotes', order: 1,
      // Off while the detail view is open (the list stays mounted behind it).
      disabled: selected.size === 0 || busy || openId !== null, run: () => void reactive(),
    },
  ]);

  // While maximized, Esc restores (matches the search-row Restore button). The
  // page-level maximize (page-fullgrid) has no grid overlay to own the key.
  useEffect(() => {
    if (!maxed) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMaxed(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [maxed]);

  // The read-only detail view — opened from a row, with the list's rows as the
  // prev/next record set. Placed after every hook so the hook order never changes.
  if (openId) {
    return (
      <CanceledQuoteDetailPage
        id={openId}
        quoteId={shown.find((r) => r.oid === openId)?.quoteId ?? null}
        siblingIds={shown.map((r) => r.oid)}
        onNavigate={setOpenId}
        onClose={() => setOpenId(null)}
        onHome={onHome}
      />
    );
  }

  return (
    <div className={`bl-page mlv-host cq-cancel-page${maxed ? ' page-fullgrid' : ''}`}>
      <div className="bl-grid-area">
        {/* Chrome (breadcrumb · title · toolbar) is HIDDEN while maximized, so the
            search row + grid fill the content area (page-fullgrid) — the nav stays
            and the search row keeps its Export + Restore controls, like Open
            Prospects (owner request 2026-10-29). */}
        {!maxed && (<>
        <div className="dp-breadcrumb">
          <a onClick={onHome} style={{ cursor: 'pointer' }}>Home</a>
          <i className="ti ti-chevron-right" />
          <span>CRM</span>
          <i className="ti ti-chevron-right" />
          <span>Accounts</span>
          <i className="ti ti-chevron-right" />
          <span>Quotes</span>
          <i className="ti ti-chevron-right" />
          <span className="dp-bc-active">Canceled Quotes</span>
        </div>
        <FormTitle pageId={PAGE_ID} loadMs={loadMs} loading={loading} auditFormName="Open Quotes" />
        <AppToolbar
          // Legacy order: Preview · Reactive · Refresh · Export to. Preview renders
          // before the `beforeRefresh` slot, so Reactive goes there to sit between.
          onPreview={() => void preview()}
          previewDisabled={selected.size !== 1}
          beforeRefresh={
            <button type="button" className="ca-btn" onClick={() => void reactive()}
                    disabled={selected.size === 0 || busy} title="Reactive (Alt+K)">
              <span className="tb-i tb-i-green">♻</span> Reactive
            </button>
          }
          onRefresh={() => void load()}
          refreshing={loading}
          exportData={{ filename: 'canceled-quotes', ...gridExportMatrix(shown, COLUMNS) }}
        />
        </>)}
        {/* Search on the LEFT, Export + Maximize on the RIGHT of the SAME row,
            OUTSIDE the grid card — the ModuleListView list look (owner request
            2026-10-29, image 1071). The grid's own header tools are hidden
            (hideHeaderTools) and its maximize is driven from the button here. */}
        <GridSearch
          value={search}
          onChange={setSearch}
          placeholder="Text to search..."
          right={
            <div className="mlv-grid-acts">
              <ExportMenu iconOnly triggerClassName="dv-gridact"
                          filename="canceled-quotes" {...gridExportMatrix(shown, COLUMNS)} />
              <button type="button" className={`dv-gridact${maxed ? ' dv-on' : ''}`}
                      onClick={() => setMaxed((v) => !v)}
                      title={maxed ? 'Restore' : 'Maximize'}
                      aria-label={maxed ? 'Restore grid' : 'Maximize grid'}>
                <i className={`ti ti-arrows-${maxed ? 'minimize' : 'maximize'}`} aria-hidden="true" />
              </button>
            </div>
          }
        />
        <AppDataGrid<CanceledQuoteRow>
          rows={shown}
          columns={COLUMNS}
          getRowId={(r) => r.oid}
          loading={loading}
          selectable
          selectedIds={selected}
          onSelectionChange={setSelected}
          onRowDoubleClick={(r) => setOpenId(r.oid)}
          hideHeaderTools
          defaultPageSize={20}
          emptyText={search ? 'No results match your search.' : 'No data to display'}
        />
      </div>
    </div>
  );
}
